import type { ConfigProfile } from '../services/api/deviceConfiguration';

const PROFILE_COMMAND_LABEL_OVERRIDES: Partial<
    Record<ConfigProfile, Partial<Record<string, string>>>
> = {
    high_current: {
        PFRH: 'LED Reset Hours',
    },
};

/**
 * Centralized Command Definitions
 * 
 * This file contains all device command configurations in one location.
 * To add a new command:
 * 1. Add the command key to COMMAND_DEFINITIONS
 * 2. Define the label and all valid values with their descriptions
 * 3. The rest of the application will automatically pick it up
 */

export interface CommandValue {
    value: string;
    label: string;
}

export interface CommandDefinition {
    key: string;
    label: string;
    values: CommandValue[];
}

/** GNSS value for auto-update location (4 consecutive days) */
export const GNSS_AUTO_UPDATE_DAYS = '4';

/** GNSS value that enables GPS on the device */
export const GNSS_ENABLE_VALUE = '1';

/** GNSS value that disables GPS on the device */
export const GNSS_DISABLE_VALUE = '0';

/** @deprecated Use GNSS_AUTO_UPDATE_DAYS */
export const GNSS_STANDARD_UPDATE_DAYS = GNSS_AUTO_UPDATE_DAYS;

export type GnssGpsMode = 'auto' | 'enable' | 'disable';

export const GNSS_GPS_MODE_PAYLOAD: Record<GnssGpsMode, string> = {
    auto: GNSS_AUTO_UPDATE_DAYS,
    enable: GNSS_ENABLE_VALUE,
    disable: GNSS_DISABLE_VALUE,
};

/**
 * All supported device commands
 */
export const COMMAND_DEFINITIONS: Record<string, CommandDefinition> = {
    CRST: {
        key: 'CRST',
        label: 'Current Reset',
        values: [
            { value: '0', label: 'Off' },
            { value: '1', label: '>50% Pre-fault Load' },
            { value: '2', label: '>5A' },
        ],
    },
    CURVE: {
        key: 'CURVE',
        label: 'Trip Settings',
        values: [
            { value: '0', label: 'Low Minimum (60A/80A)' },
            { value: '1', label: 'Standard (120A/200A)' },
            { value: '2', label: 'Fire Mitigation' },
        ],
    },
    FIXT: {
        key: 'FIXT',
        label: 'Trip Settings',
        values: [
            { value: '400', label: '400' },
            { value: '800', label: '800' },
            { value: '1200', label: '1200' },
            { value: '1600', label: '1600' },
        ],
    },
    OVER: {
        key: 'OVER',
        label: 'Overcurrent Alert',
        values: [
            { value: '0', label: 'Off' },
            { value: '1', label: 'On' },
        ],
    },
    GNSS: {
        key: 'GNSS',
        label: 'GPS Updates',
        values: [], // Dynamic: 0 = off, N = consecutive days
    },
    SHELF: {
        key: 'SHELF',
        label: 'Shelf Mode',
        values: [], // Flag command: sending SHELF enables shelf mode
    },
    TFRH: {
        key: 'TFRH',
        label: 'Temporary Fault Reset Hours',
        values: [
            { value: '2', label: '2 hours' },
            { value: '4', label: '4 hours' },
            { value: '8', label: '8 hours' },
            { value: '12', label: '12 hours' },
            { value: '24', label: '24 hours' },
            { value: '48', label: '48 hours' },
            { value: '168', label: '168 hours (1 week)' },
        ],
    },
    PFRH: {
        key: 'PFRH',
        label: 'Permanent Fault Reset Hours',
        values: [
            { value: '2', label: '2 hours' },
            { value: '4', label: '4 hours' },
            { value: '8', label: '8 hours' },
            { value: '12', label: '12 hours' },
            { value: '24', label: '24 hours' },
            { value: '48', label: '48 hours' },
            { value: '168', label: '168 hours (1 week)' },
        ],
    },
    RT: {
        key: 'RT',
        label: 'Radio Timeout',
        values: [], // Dynamic value
    },
    CONFIG: {
        key: 'CONFIG',
        label: 'Configuration Profile',
        values: [], // Dynamic value
    },
    GPS: {
        key: 'GPS',
        label: 'GPS Settings',
        values: [], // Dynamic value
    },
} as const;

/**
 * Get the human-readable label for a command type
 */
export const getCommandLabel = (commandKey: string, profile?: ConfigProfile): string => {
    if (profile) {
        const override = PROFILE_COMMAND_LABEL_OVERRIDES[profile]?.[commandKey];
        if (override) return override;
    }
    return COMMAND_DEFINITIONS[commandKey]?.label || commandKey;
};

/**
 * Get the human-readable label for a specific command value
 */
export const getCommandValueLabel = (commandKey: string, value: string): string | null => {
    const command = COMMAND_DEFINITIONS[commandKey];
    if (!command) return null;

    const valueOption = command.values.find(v => v.value === value);
    return valueOption?.label || null;
};

export const getGnssValueLabel = (value: string): string => {
    const v = String(value).trim();
    if (v === GNSS_DISABLE_VALUE) return 'Disable GPS';
    if (v === GNSS_ENABLE_VALUE) return 'Enable GPS';
    if (v === GNSS_AUTO_UPDATE_DAYS) {
        return 'Auto-update location (4 consecutive days)';
    }
    return v;
};

/**
 * Get the full readable description for a command (key + value)
 * Example: getReadableCommand('CRST', '0') => 'Current Reset: Off'
 */
export const getReadableCommand = (key: string, val: string): string => {
    const k = String(key).trim();
    const v = String(val).trim();

    const command = COMMAND_DEFINITIONS[k];
    if (!command) {
        return `${k}=${v}`;
    }

    // For commands with predefined values
    const valueLabel = getCommandValueLabel(k, v);
    if (valueLabel) {
        return `${command.label}: ${valueLabel}`;
    }

    // For dynamic value commands (RT, CONFIG, GPS, GNSS, SHELF)
    if (command.values.length === 0) {
        if (k === 'GNSS') {
            return `${command.label}: ${getGnssValueLabel(v)}`;
        }
        if (k === 'SHELF') {
            return command.label;
        }
        return `${command.label}: ${v}`;
    }

    // Fallback
    return `${k}=${v}`;
};

/**
 * Get readable description from a full command string
 * Example: getReadableCommandFromString('CRST=0') => 'Current Reset: Off'
 */
export const getReadableCommandFromString = (commandStr: string): string => {
    if (!commandStr) return '';

    const trimmed = commandStr.trim();
    const parts = trimmed.split('=');
    if (parts.length === 2) {
        return getReadableCommand(parts[0].trim(), parts[1].trim());
    }

    if (COMMAND_DEFINITIONS[trimmed]) {
        return getReadableCommand(trimmed, '');
    }

    return commandStr;
};

/**
 * Get configuration entries in a stable display order (known commands first).
 */
export const getConfigurationEntries = (
    configuration: object | null | undefined
): [string, string][] => {
    if (!configuration) return [];

    const commandOrder = Object.keys(COMMAND_DEFINITIONS);

    return Object.entries(configuration as Record<string, string | undefined | null>)
        .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '')
        .sort(([a], [b]) => {
            const aIndex = commandOrder.indexOf(a);
            const bIndex = commandOrder.indexOf(b);
            if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;
            return aIndex - bIndex;
        }) as [string, string][];
};

/**
 * Get all command options formatted for select inputs
 */
export const getCommandOptions = () => {
    return Object.entries(COMMAND_DEFINITIONS)
        .filter(([_, cmd]) => cmd.values.length > 0) // Only include commands with predefined values
        .reduce((acc, [key, cmd]) => ({
            ...acc,
            [key]: {
                label: cmd.label,
                options: cmd.values,
            },
        }), {} as Record<string, { label: string; options: CommandValue[] }>);
};

/**
 * Map of command type labels for conflict detection
 */
export const COMMAND_TYPE_LABELS: Record<string, string> = Object.entries(COMMAND_DEFINITIONS).reduce(
    (acc, [key, cmd]) => ({
        ...acc,
        [key]: cmd.label,
        // Also support friendly names
        [cmd.label.replace(/ /g, '')]: cmd.label,
    }),
    {} as Record<string, string>
);

/**
 * Get value options as a simple key-value map
 * Example: { '0': 'Off', '1': 'On', '2': 'Luma Mode' }
 */
export const getCommandValueMap = (commandKey: string): Record<string, string> => {
    const command = COMMAND_DEFINITIONS[commandKey];
    if (!command) return {};

    return command.values.reduce((acc, v) => ({
        ...acc,
        [v.value]: v.label,
    }), {} as Record<string, string>);
};

/**
 * Get all command value maps
 */
export const getAllCommandValueMaps = (): Record<string, Record<string, string>> => {
    return Object.keys(COMMAND_DEFINITIONS).reduce((acc, key) => ({
        ...acc,
        [key]: getCommandValueMap(key),
    }), {} as Record<string, Record<string, string>>);
};

/**
 * Get all command labels
 */
export const getAllCommandLabels = (): Record<string, string> => {
    return Object.keys(COMMAND_DEFINITIONS).reduce((acc, key) => ({
        ...acc,
        [key]: COMMAND_DEFINITIONS[key].label,
    }), {} as Record<string, string>);
};

/** Default trip setting value for high-current (FIXT) devices */
export const HIGH_CURRENT_DEFAULT_TRIP_VALUE = '800';

/** Default trip setting value for standard (CURVE) devices */
export const STANDARD_DEFAULT_TRIP_VALUE = '1';

/**
 * Trip settings options for send-command UI based on device type.
 */
export const getTripSettingsOptions = (isHighCurrent: boolean): {
    key: string;
    label: string;
    options: CommandValue[];
} => {
    const key = isHighCurrent ? 'FIXT' : 'CURVE';
    const cmd = COMMAND_DEFINITIONS[key];
    return {
        key,
        label: cmd.label,
        options: cmd.values,
    };
};

const CONFIG_EDITOR_COMMAND_KEYS: Record<ConfigProfile, string[]> = {
    standard: ['CRST', 'CURVE', 'OVER', 'TFRH', 'PFRH'],
    high_current: ['FIXT', 'PFRH'],
};

/**
 * Command keys shown in template editors for a given configuration profile.
 */
export const getConfigEditorCommandKeys = (profile: ConfigProfile): string[] => {
    return CONFIG_EDITOR_COMMAND_KEYS[profile];
};

/**
 * Command options for template editors, filtered by configuration profile.
 */
export const getConfigEditorOptions = (
    profile: ConfigProfile
): Record<string, { label: string; options: CommandValue[] }> => {
    return getConfigEditorCommandKeys(profile).reduce((acc, key) => {
        const cmd = COMMAND_DEFINITIONS[key];
        if (!cmd || cmd.values.length === 0) return acc;
        return {
            ...acc,
            [key]: {
                label: getCommandLabel(key, profile),
                options: cmd.values,
            },
        };
    }, {} as Record<string, { label: string; options: CommandValue[] }>);
};

